import { useContext, useEffect, useRef, useState, type CSSProperties, type PropsWithChildren, type SubmitEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Element, useEditor } from "@nkitaa/builder-engine";
import { IconAlertTriangle, IconCheck, IconClock, IconClockHour4 } from "@tabler/icons-react";
import { useWBTheme, useEventTimezone } from "../theme/WBThemeRuntimeProvider";
import { useWBLang } from "../lang/WBLanguageRuntimeProvider";
import { resolveText, type LangText } from "../lib/resolve-text";
import { submitLogin, submitRegister } from "../../lib/runtimePage";
import { ApiError } from "../../lib/api";
import { parseCapacityRefusal } from "../../lib/sessionsApi";
import { submitErrorText } from "../lib/submit-error";
import { t } from "../locale";
import {
  type BorderSide,
  type FieldSpacing,
  type RuntimeProps,
  type SubmitState,
  ASSET_BASE,
  RegisterOutcomeContext,
  StatusMessage,
  SubmitStateContext,
  WB_VALIDATE_EVENT,
  classNames,
  collectFieldValues,
  extractUtilityClasses,
  getBorderClasses,
  isVisible,
  notifyAuthChanged,
  parseFormMaxWidth,
  sanitizeImageSrc,
  sectionMinHeight,
  toCssLength,
} from "./shared";
import { revealClass, type WBReveal } from "./reveal";
import { TextChild } from "./textChild";
import { Button } from "./button";
import { ImageChild } from "./imageChild";
import { FormInput } from "./formInput";
import { getDarkBackdrop } from "../lib/contrastColor";

export function Register(props: RuntimeProps) {
  const { currentColorPalette, currentRounding } = useWBTheme();
  const appBarTransparent = useEditor((state) => {
    for (const node of Object.values(state.nodes)) {
      if (node?.data?.displayName === "AppBar") {
        return node.data.props?.transparent === true;
      }
    }
    return false;
  });
  if (!isVisible(props.isVisible as boolean | undefined)) return null;

  const padding =
    (props.padding as string | undefined) ?? "p-4 wb-md:p-6 wb-lg:p-8";
  const sectionMargin = (props.sectionMargin as string | undefined) ?? "py-0";
  const sectionHeight = (props.sectionHeight as number | undefined) ?? 60;
  const contentAlignment =
    (props.contentAlignment as "top" | "center" | "bottom" | undefined) ??
    "center";
  const border = (props.border as boolean | undefined) ?? false;
  const borderWidth = (props.borderWidth as string | undefined) ?? "border-2";
  const borderStyle =
    (props.borderStyle as string | undefined) ?? "border-solid";
  const borderSides = (props.borderSides as BorderSide[] | undefined) ?? [];
  const borderRadius = props.borderRadius as string | undefined;
  const borderColor = props.borderColor as string | undefined;
  const isVisibleImage = (props.isVisibleImage as boolean | undefined) ?? true;
  const imageLayout =
    (props.imageLayout as "background" | "none" | undefined) ?? "background";
  const backgroundColor =
    // Editor parity: Register renders on getDarkBackdrop(palette) when no
    // explicit background is stored (main-frontend Register.tsx) - the
    // template-baked white copy depends on it.
    (props.backgroundColor as string | undefined) ??
    getDarkBackdrop(currentColorPalette);
  const backgroundOverlayColor =
    (props.backgroundOverlayColor as string | undefined) ?? "#00000080";
  const backgroundImageUrl = sanitizeImageSrc(
    String(props.imageSrc ?? `${ASSET_BASE}/shared/hero.jpg`),
  );
  const backgroundType =
    (props.backgroundType as "image" | "video" | undefined) ?? "image";
  const videoSrc = props.videoSrc as string | undefined;
  const videoPosterSrc = props.videoPosterSrc as string | undefined;
  const videoLoop = (props.videoLoop as boolean | undefined) ?? true;
  const reveal = props.reveal as WBReveal | undefined;
  const roundedClasses = extractUtilityClasses(
    borderRadius ?? currentRounding,
    "rounded",
  );
  const borderClasses = getBorderClasses(
    border,
    borderWidth,
    borderStyle,
    borderSides,
  );
  const alignmentClass =
    contentAlignment === "center"
      ? "justify-center"
      : contentAlignment === "bottom"
        ? "justify-end"
        : "justify-start";

  const backgroundStyle: CSSProperties =
    isVisibleImage && backgroundType === "image" && imageLayout === "background" && backgroundImageUrl
      ? {
          backgroundImage: `url(${backgroundImageUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }
      : { backgroundColor };

  return (
    <section
      className={classNames(
        "websitebuilder w-full flex justify-center",
        sectionMargin,
      )}
    >
      <div
        className={classNames(
          "relative w-full flex flex-col items-center overflow-hidden",
          padding,
          alignmentClass,
          borderClasses,
          roundedClasses,
          revealClass(reveal),
        )}
        style={{
          ...backgroundStyle,
          minHeight: sectionMinHeight(sectionHeight, appBarTransparent),
          borderColor: borderColor ?? currentColorPalette.secondary,
          border: border ? undefined : "none",
          borderRadius: roundedClasses
            ? undefined
            : toCssLength(borderRadius ?? currentRounding),
        }}
      >
        {isVisibleImage && backgroundType === "video" && videoSrc ? (
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src={videoSrc}
            poster={videoPosterSrc}
            autoPlay
            loop={videoLoop}
            muted
            playsInline
            disablePictureInPicture
          />
        ) : null}
        {isVisibleImage && (imageLayout === "background" || backgroundType === "video") ? (
          <div
            className="absolute inset-0"
            style={{ backgroundColor: backgroundOverlayColor }}
          />
        ) : null}
        <div className="relative z-10 w-full flex items-center justify-center">
          <Element is={RegisterChild} id="registerChild" canvas />
        </div>
      </div>
    </section>
  );
}

export function Login(props: RuntimeProps) {
  const { currentColorPalette, currentRounding } = useWBTheme();
  const appBarTransparent = useEditor((state) => {
    for (const node of Object.values(state.nodes)) {
      if (node?.data?.displayName === "AppBar") {
        return node.data.props?.transparent === true;
      }
    }
    return false;
  });
  if (!isVisible(props.isVisible as boolean | undefined)) return null;

  const padding =
    (props.padding as string | undefined) ?? "p-4 wb-md:p-6 wb-lg:p-8";
  const sectionMargin = (props.sectionMargin as string | undefined) ?? "py-0";
  const sectionHeight = (props.sectionHeight as number | undefined) ?? 60;
  const contentAlignment =
    (props.contentAlignment as "top" | "center" | "bottom" | undefined) ??
    "center";
  const border = (props.border as boolean | undefined) ?? false;
  const borderWidth = (props.borderWidth as string | undefined) ?? "border-2";
  const borderStyle =
    (props.borderStyle as string | undefined) ?? "border-solid";
  const borderSides = (props.borderSides as BorderSide[] | undefined) ?? [];
  const borderRadius = props.borderRadius as string | undefined;
  const borderColor = props.borderColor as string | undefined;
  const isVisibleImage = (props.isVisibleImage as boolean | undefined) ?? true;
  const imageLayout =
    (props.imageLayout as "background" | "none" | undefined) ?? "background";
  const backgroundColor =
    // Editor parity: Login falls back to the theme background color
    // (main-frontend Login.tsx), not a hardcoded near-white.
    (props.backgroundColor as string | undefined) ??
    currentColorPalette.background;
  const backgroundOverlayColor =
    (props.backgroundOverlayColor as string | undefined) ?? "#00000080";
  const backgroundImageUrl = sanitizeImageSrc(
    String(props.imageSrc ?? `${ASSET_BASE}/shared/hero.jpg`),
  );
  const backgroundType =
    (props.backgroundType as "image" | "video" | undefined) ?? "image";
  const videoSrc = props.videoSrc as string | undefined;
  const videoPosterSrc = props.videoPosterSrc as string | undefined;
  const videoLoop = (props.videoLoop as boolean | undefined) ?? true;
  const reveal = props.reveal as WBReveal | undefined;
  const roundedClasses = extractUtilityClasses(
    borderRadius ?? currentRounding,
    "rounded",
  );
  const borderClasses = getBorderClasses(
    border,
    borderWidth,
    borderStyle,
    borderSides,
  );
  const alignmentClass =
    contentAlignment === "center"
      ? "justify-center"
      : contentAlignment === "bottom"
        ? "justify-end"
        : "justify-start";

  const backgroundStyle: CSSProperties =
    isVisibleImage && backgroundType === "image" && imageLayout === "background" && backgroundImageUrl
      ? {
          backgroundImage: `url(${backgroundImageUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }
      : { backgroundColor };

  return (
    <section
      className={classNames(
        "websitebuilder w-full flex justify-center",
        sectionMargin,
      )}
    >
      <div
        className={classNames(
          "relative w-full flex flex-col items-center overflow-hidden",
          padding,
          alignmentClass,
          borderClasses,
          roundedClasses,
          revealClass(reveal),
        )}
        style={{
          ...backgroundStyle,
          minHeight: sectionMinHeight(sectionHeight, appBarTransparent),
          borderColor: borderColor ?? currentColorPalette.secondary,
          border: border ? undefined : "none",
          borderRadius: roundedClasses
            ? undefined
            : toCssLength(borderRadius ?? currentRounding),
        }}
      >
        {isVisibleImage && backgroundType === "video" && videoSrc ? (
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src={videoSrc}
            poster={videoPosterSrc}
            autoPlay
            loop={videoLoop}
            muted
            playsInline
            disablePictureInPicture
          />
        ) : null}
        {isVisibleImage && (imageLayout === "background" || backgroundType === "video") ? (
          <div
            className="absolute inset-0"
            style={{ backgroundColor: backgroundOverlayColor }}
          />
        ) : null}
        <div className="relative z-10 w-full flex items-center justify-center">
          <Element is={LoginChild} id="loginChild" canvas />
        </div>
      </div>
    </section>
  );
}

// C4 (W2-A): the only field variables the worker's registerValueMap folds
// from the TOP LEVEL of a /auth/register body. Everything else must be
// nested under `values:{}` or the worker drops it silently.
const LEGACY_REGISTER_FIELDS: ReadonlySet<string> = new Set([
  "firstName",
  "lastName",
  "email",
  "company",
  "note",
]);

export function RegisterChild({
  isVisible = true,
  children,
  backgroundColor,
  padding = "p-4 wb-md:p-6 wb-lg:p-8",
  distTexts = "space-y-1 wb-md:space-y-2 wb-lg:space-y-3",
  formTextSpacing = "mt-3 wb-md:mt-4 wb-lg:mt-5",
  fieldSpacing = "medium",
  buttonFormSpacing = "mt-3 wb-md:mt-4 wb-lg:mt-5",
  buttonAlign = "left",
  maxWidth = 80,
  width = 800,
  borderRadius,
  border = true,
  borderColor,
  borderStyle = "border-solid",
  borderWidth = "border",
  borderSides = ["top", "right", "bottom", "left"],
  ruleRegistrationBlocked = false,
  registrationBlockedMessage,
}: PropsWithChildren<{
  isVisible?: boolean;
  backgroundColor?: string;
  padding?: string;
  distTexts?: string;
  formTextSpacing?: string;
  fieldSpacing?: FieldSpacing;
  buttonFormSpacing?: string;
  buttonAlign?: "left" | "right" | "center";
  maxWidth?: number;
  width?: number;
  borderRadius?: string;
  border?: boolean;
  borderColor?: string;
  borderStyle?: string;
  borderWidth?: string;
  borderSides?: BorderSide[];
  ruleRegistrationBlocked?: boolean;
  registrationBlockedMessage?: string;
}>) {
  const { currentColorPalette, currentRounding, loginAfterRegister } = useWBTheme();
  const eventTz = useEventTimezone();
  const [state, setState] = useState<SubmitState>({ loading: false });
  const [outcome, setOutcome] = useState<{ type?: string; waitlistPosition?: number }>({});
  // H1 (W2-A): synchronous in-flight lock. The Button's loading-disable
  // alone loses a genuine double-click race (both clicks land before React
  // repaints the disabled state) → two POSTs → duplicate guests.
  const inFlightRef = useRef(false);
  const roundedClasses = extractUtilityClasses(
    borderRadius ?? currentRounding,
    "rounded",
  );
  const borderClasses = getBorderClasses(
    border ?? true,
    borderWidth,
    borderStyle,
    borderSides,
  );
  const fieldSpacingClass =
    fieldSpacing === "extraSmall"
      ? "gap-2"
      : fieldSpacing === "small"
        ? "gap-3"
        : fieldSpacing === "large"
          ? "gap-6"
          : fieldSpacing === "extraLarge"
            ? "gap-8"
            : "gap-4";

  if (!isVisible) return null;

  const isPendingApproval = loginAfterRegister === false;

  async function onSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;

    // Dispatch validation event — each FormInput increments errorCount
    const detail = { errorCount: 0 };
    form.dispatchEvent(new CustomEvent(WB_VALIDATE_EVENT, { detail }));
    if (detail.errorCount > 0) return;

    // H1 (W2-A): reject re-entry while a submit is in flight.
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    setState({ loading: true });
    const { values, companions, checkboxes } = collectFieldValues(form, eventTz);

    // C4 (W2-A): the worker's registerValueMap only folds the legacy
    // top-level keys (firstName/lastName/email/company/note); everything
    // else must ride in `values:{}` — and TYPED (String() would mangle
    // MULTI_SELECT arrays to "a,b" and BOOLEAN to "true", which the worker
    // validator rejects). Legacy 5 stay top-level for compat.
    const payload: Record<string, unknown> = {};
    const nestedValues: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(values)) {
      if (LEGACY_REGISTER_FIELDS.has(key)) {
        payload[key] = val == null ? "" : String(val);
      } else {
        nestedValues[key] = val;
      }
    }
    if (Object.keys(nestedValues).length > 0) payload.values = nestedValues;
    // H2 (W2-A): companion-scoped inputs were collected but dropped by the
    // old `const { values } = …` destructure — send them (the worker fully
    // supports body.companions incl. atomic group capacity).
    if (companions.length > 0) {
      payload.companions = companions.map((companion) => ({ values: companion }));
    }
    // PB-RCS-1 (2026-10-04): the consent evidence. Every Checkboxes row inside
    // the form — ticked OR unticked, bound or not — travels top-level exactly
    // as collected (`{text, required, checked, inputFieldVariable?}`), the
    // shape the Worker's `guest_consents` writer records with the moment and
    // the hash of the wording. A bound box still ALSO rides in `values` as its
    // legacy boolean column (`emailOptIn`). Always an array — `[]` says "this
    // form showed no consent box", a MISSING key says "legacy runtime" — the
    // same contract the data-input submit has carried (dataInput.tsx).
    payload.checkboxes = checkboxes;

    try {
      const response = await submitRegister(payload);
      // H3 (W2-A): the worker puts the ACCEPTANCE state in `outcome`
      // ("SYSTEM" for a waitlisted guest) — WAITLISTED travels in
      // `guestState`. Key the waitlist card on guestState, not outcome.
      const responseOutcome = response.outcome;
      const nextOutcome: { type?: string; waitlistPosition?: number } = {};
      if (response.guestState === "WAITLISTED") nextOutcome.type = "WAITLISTED";
      else if (responseOutcome) nextOutcome.type = responseOutcome;
      if (response.waitlistPosition != null) nextOutcome.waitlistPosition = response.waitlistPosition;
      setOutcome(nextOutcome);
      if (responseOutcome === "ACCEPTED" && !isPendingApproval) {
        notifyAuthChanged();
      }
      setState({
        loading: false,
        success: String(
          response.message ?? "Registration successful!",
        ),
      });
      form.reset();
    } catch (error) {
      // 403 capacity refusals (full segment quota, companion cap) arrived as
      // the worker's raw English `message` — and the quota one carried the
      // organiser's internal segment name inside it (`Quota "VIP-Kontingent"
      // is full`). Neutral, localised, no internal name: decision 2026-08-12.
      const refusal =
        error instanceof ApiError ? parseCapacityRefusal(error.data) : null;
      if (refusal !== null) {
        setState({
          loading: false,
          error:
            refusal.kind === "quotaFull"
              ? t("registrationSegmentFull")
              : t("companionLimitReached"),
        });
        return;
      }
      // Audit 2026-09-23 (4f): errorCode / status ⇒ localized; a 403 keeps
      // the operator's sentence.
      setState({
        loading: false,
        error: submitErrorText(error, "Registration failed."),
      });
    } finally {
      inFlightRef.current = false;
    }
  }

  const buttonAlignClass =
    buttonAlign === "right"
      ? "items-end"
      : buttonAlign === "center"
        ? "items-center"
        : "items-start";

  // Mirror the editor (Register.tsx): when registration is blocked, render a
  // standalone centered message card and HIDE the 2-column grid. The text
  // column is hidden on success (the form card then occupies col 1, same as the
  // editor). The grid only splits into two columns at wb-lg.
  const showTextBlock = !state.success;

  return (
    <>
      {ruleRegistrationBlocked && (
        <div
          className={classNames(
            "flex flex-col items-center justify-center w-full mx-auto",
            padding,
            borderClasses,
            roundedClasses,
          )}
          style={{
            maxWidth: parseFormMaxWidth(width, maxWidth),
            backgroundColor: backgroundColor ?? currentColorPalette.background,
            borderColor:
              (border ?? true)
                ? (borderColor ?? currentColorPalette.secondary)
                : undefined,
            borderRadius: roundedClasses
              ? undefined
              : toCssLength(borderRadius ?? currentRounding),
          }}
        >
          <div className="text-center py-8">
            <IconAlertTriangle
              size={48}
              className="mx-auto mb-4 opacity-60"
              style={{ color: currentColorPalette.primary }}
            />
            <p className="text-base" style={{ color: currentColorPalette.primary }}>
              {registrationBlockedMessage || t("registrationClosed")}
            </p>
          </div>
        </div>
      )}

      <div
        className={classNames(
          "grid grid-cols-1 wb-lg:grid-cols-2 gap-8 wb-lg:gap-14 items-center w-full max-w-[1100px] mx-auto",
          ruleRegistrationBlocked ? "hidden" : undefined,
        )}
      >
        {/* Left column: heading + subtitle on the branded backdrop */}
        {showTextBlock && (
          <div className={classNames("w-full", distTexts)}>
            <Element is={ImageChild} id="logo" />
            <Element is={TextChild} id="headline" />
            <Element is={TextChild} id="header" />
            <Element is={TextChild} id="text" />
          </div>
        )}

        {/* Right column: form card */}
        <form
          onSubmit={onSubmit}
          noValidate
          className={classNames(
            "flex flex-col w-full shadow-[0_24px_60px_rgba(0,0,0,0.18)]",
            padding,
            borderClasses,
            roundedClasses,
          )}
          style={{
            backgroundColor: backgroundColor ?? currentColorPalette.background,
            borderColor:
              (border ?? true)
                ? (borderColor ?? currentColorPalette.secondary)
                : undefined,
            borderRadius: roundedClasses
              ? undefined
              : toCssLength(borderRadius ?? currentRounding),
          }}
        >
          {state.success ? (
            // Audit 2026-09-23 (4b): the props below are SEEDS the engine
            // reads only when it creates the node; the saved success group
            // kept `isWaitlisted:false`. The context is what the card reads.
            <RegisterOutcomeContext.Provider
              value={{
                isPendingApproval: isPendingApproval || outcome.type === "PENDING",
                isWaitlisted: outcome.type === "WAITLISTED",
                waitlistPosition: outcome.waitlistPosition,
              }}
            >
              <Element
                is={RegisterSuccessGroup}
                id="successGroup"
                isPendingApproval={isPendingApproval || outcome.type === "PENDING"}
                isWaitlisted={outcome.type === "WAITLISTED"}
                waitlistPosition={outcome.waitlistPosition}
                successMessage={state.success}
              />
            </RegisterOutcomeContext.Provider>
          ) : (
            <div className="flex flex-col grow justify-center">
              <div
                className={classNames(
                  "w-full flex flex-col wb-sm:grid wb-sm:grid-cols-12",
                  fieldSpacingClass,
                  formTextSpacing,
                )}
              >
                {children}
              </div>

              <div
                className={classNames(
                  "w-full flex-col space-y-2",
                  buttonFormSpacing,
                  buttonAlignClass,
                  "flex",
                )}
              >
                {/* `loading` on <Element> is only a seed (audit 2026-09-23, 4b). */}
                <SubmitStateContext.Provider
                  value={{ isSubmitted: Boolean(state.success), loading: state.loading }}
                >
                  <Element
                    is={Button}
                    id="primary"
                    buttonType="submit"
                    loading={state.loading}
                  />
                </SubmitStateContext.Provider>
                <Element is={Button} id="secondary" />
              </div>

              <StatusMessage state={state} />
            </div>
          )}
        </form>
      </div>
    </>
  );
}

export function LoginChild({
  isVisible = true,
  children,
  backgroundColor,
  padding = "p-4 wb-md:p-6 wb-lg:p-8",
  distTexts = "space-y-1 wb-md:space-y-2 wb-lg:space-y-3",
  formTextSpacing = "mt-3 wb-md:mt-4 wb-lg:mt-5",
  fieldSpacing = "medium",
  buttonFormSpacing = "mt-3 wb-md:mt-4 wb-lg:mt-5",
  buttonAlign = "left",
  maxWidth = 80,
  width = 800,
  borderRadius,
  border = true,
  borderColor,
  borderStyle = "border-solid",
  borderWidth = "border",
  borderSides = ["top", "right", "bottom", "left"],
  ruleLoginBlocked = false,
  loginBlockedMessage,
}: PropsWithChildren<{
  isVisible?: boolean;
  backgroundColor?: string;
  padding?: string;
  distTexts?: string;
  formTextSpacing?: string;
  fieldSpacing?: FieldSpacing;
  buttonFormSpacing?: string;
  buttonAlign?: "left" | "right" | "center";
  maxWidth?: number;
  width?: number;
  borderRadius?: string;
  border?: boolean;
  borderColor?: string;
  borderStyle?: string;
  borderWidth?: string;
  borderSides?: BorderSide[];
  ruleLoginBlocked?: boolean;
  loginBlockedMessage?: LangText;
}>) {
  const { currentColorPalette, currentRounding, homeSlug } = useWBTheme();
  const { currentLang, primaryLanguage } = useWBLang();
  const eventTz = useEventTimezone();
  const navigate = useNavigate();
  const [state, setState] = useState<SubmitState>({ loading: false });
  const formRef = useRef<HTMLFormElement | null>(null);
  const roundedClasses = extractUtilityClasses(
    borderRadius ?? currentRounding,
    "rounded",
  );

  // Credentials decoded from the URL and stashed by App.tsx before any route
  // mounted. Consuming them here keeps them out of history/Referer even when
  // the guest is already authenticated and bypasses /login entirely.
  useEffect(() => {
    if (ruleLoginBlocked) {
      // Drop any stashed credentials so a later unblocked visit can't pick
      // up stale prefill from this guest's email click.
      sessionStorage.removeItem("_auth_prefill");
      return;
    }
    const raw = sessionStorage.getItem("_auth_prefill");
    if (!raw) return;
    // H41: NOT consumed yet. It used to be deleted here, before anything had
    // been filled — and the two prefill attempts below (now + one rAF) both
    // ran ~2 frames before runtime-materialized inputs mount. On any page
    // whose login inputs are not serialized, the credential was therefore
    // consumed and then thrown away: the guest landed on an EMPTY form with
    // the URL already scrubbed. The stash is now cleared only once the values
    // are actually in the inputs (or the window below expires).
    let username: string | null = null;
    let code: string | null = null;
    try {
      const parsed = JSON.parse(raw) as { username?: string; code?: string };
      username = parsed.username ?? null;
      code = parsed.code ?? null;
    } catch {
      return;
    }
    if (!username && !code) return;

    /** True once the values are actually in the DOM. */
    const prefill = (): boolean => {
      const form = formRef.current;
      if (!form) return false;
      const userInput = form.querySelector<HTMLInputElement>(
        'input[data-field-variable="username"], [data-field-variable="username"] input',
      );
      const codeInput = form.querySelector<HTMLInputElement>(
        'input[data-field-variable="code"], [data-field-variable="code"] input',
      );
      // Both halves have to be there before we call it done: filling only the
      // username and dropping the code would be the same silent loss in a
      // smaller costume.
      const needUser = Boolean(username);
      const needCode = Boolean(code);
      if ((needUser && !userInput) || (needCode && !codeInput)) return false;
      if (userInput && username) {
        userInput.value = username;
        userInput.dispatchEvent(new Event("input", { bubbles: true }));
      }
      if (codeInput && code) {
        codeInput.value = code;
        codeInput.dispatchEvent(new Event("input", { bubbles: true }));
      }
      return true;
    };

    // H41: wait for the inputs instead of guessing when they mount. A
    // MutationObserver on the form's subtree fires on the very commit that
    // materializes them, so there is no polling interval to tune and no
    // frame count to be wrong about. The timeout is the give-up condition —
    // without it a page that never renders a login form would keep the
    // credential in sessionStorage indefinitely, which is the stale-prefill
    // hazard the sibling `ruleLoginBlocked` branch exists to prevent.
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      sessionStorage.removeItem("_auth_prefill");
      observer.disconnect();
      clearTimeout(timer);
    };
    const attempt = () => {
      if (done) return;
      if (prefill()) finish();
    };
    const observer = new MutationObserver(attempt);
    const timer = setTimeout(finish, 10_000);

    attempt();
    if (!done) {
      // Observe the document rather than formRef: on the race this fixes, the
      // form element itself is what mounts late.
      observer.observe(document.body, { childList: true, subtree: true });
    }
    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, [ruleLoginBlocked]);
  const borderClasses = getBorderClasses(
    border ?? true,
    borderWidth,
    borderStyle,
    borderSides,
  );
  const fieldSpacingClass =
    fieldSpacing === "extraSmall"
      ? "gap-2"
      : fieldSpacing === "small"
        ? "gap-3"
        : fieldSpacing === "large"
          ? "gap-6"
          : fieldSpacing === "extraLarge"
            ? "gap-8"
            : "gap-4";

  if (!isVisible) return null;

  async function onSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setState({ loading: true });

    const form = event.currentTarget;
    const { values } = collectFieldValues(form, eventTz);

    try {
      const response = await submitLogin({
        username: String(values.username ?? ""),
        code: String(values.code ?? ""),
      });

      if (response.success === false) {
        throw new Error(String(response.message ?? "Login failed."));
      }

      notifyAuthChanged();
      setState({
        loading: false,
        success: String(response.message ?? "Login submitted."),
      });
      form.reset();
      const homeTarget =
        homeSlug && homeSlug.trim()
          ? homeSlug.startsWith("/")
            ? homeSlug
            : `/${homeSlug}`
          : "/";
      navigate(homeTarget, { replace: true });
    } catch (error) {
      // Audit 2026-09-23 (4f): 401/429/503 ⇒ localized; a 403 keeps the
      // operator's sentence.
      setState({
        loading: false,
        error: submitErrorText(error, "Login failed.", { closedKey: "loginClosed" }),
      });
    }
  }

  const buttonAlignClass =
    buttonAlign === "right"
      ? "items-end"
      : buttonAlign === "center"
        ? "items-center"
        : "items-start";

  return (
    <form
      ref={formRef}
      onSubmit={onSubmit}
      noValidate
      className={classNames(
        "flex flex-col w-full mx-auto shadow-[0_12px_40px_rgba(0,0,0,0.08)]",
        padding,
        borderClasses,
        roundedClasses,
      )}
      style={{
        maxWidth: parseFormMaxWidth(width, maxWidth),
        backgroundColor: backgroundColor ?? currentColorPalette.background,
        borderColor:
          (border ?? true)
            ? (borderColor ?? currentColorPalette.secondary)
            : undefined,
        borderRadius: roundedClasses
          ? undefined
          : toCssLength(borderRadius ?? currentRounding),
      }}
    >
      {ruleLoginBlocked ? (
        <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
          <IconAlertTriangle size={48} className="mb-4 opacity-60" style={{ color: currentColorPalette.primary }} />
          <p style={{ color: currentColorPalette.primary, fontSize: "1rem", lineHeight: "1.5", maxWidth: 360 }}>
            {resolveText(loginBlockedMessage, currentLang, primaryLanguage) || t("loginClosed")}
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-col grow items-center justify-center">
            <div className={classNames("w-full", distTexts)}>
              <Element is={ImageChild} id="logo" />
              <Element is={TextChild} id="headline" />
              <Element is={TextChild} id="header" />
              <Element is={TextChild} id="text" />
            </div>

            <div
              className={classNames(
                "w-full flex flex-col wb-sm:grid wb-sm:grid-cols-12",
                fieldSpacingClass,
                formTextSpacing,
              )}
            >
              <Element
                is={FormInput}
                id="loginForm"
                inputFieldVariable="username"
                label="Username"
                requiredField
                gridArea="col-span-6"
              />
              <Element
                is={FormInput}
                id="codeForm"
                inputFieldVariable="code"
                label="Code"
                requiredField
                gridArea="col-span-6"
              />
              {children}
            </div>

            <div
              className={classNames(
                "w-full flex-col space-y-2",
                buttonFormSpacing,
                buttonAlignClass,
                "flex",
              )}
            >
              {/* `loading` on <Element> is only a seed (audit 2026-09-23, 4b). */}
              <SubmitStateContext.Provider
                value={{ isSubmitted: Boolean(state.success), loading: state.loading }}
              >
                <Element
                  is={Button}
                  id="primary"
                  buttonType="submit"
                  loading={state.loading}
                />
              </SubmitStateContext.Provider>
              <Element is={Button} id="secondary" />
            </div>
          </div>

          <StatusMessage state={state} />
        </>
      )}
    </form>
  );
}

export function RegisterSuccessGroup({
  iconColor,
  iconBackgroundColor,
  backgroundColor,
  isPendingApproval: isPendingApprovalProp = false,
  isWaitlisted: isWaitlistedProp = false,
  waitlistPosition: waitlistPositionProp,
  children,
}: PropsWithChildren<{
  iconColor?: string;
  iconBackgroundColor?: string;
  backgroundColor?: string;
  isPendingApproval?: boolean;
  isWaitlisted?: boolean;
  waitlistPosition?: number;
  successMessage?: string;
}>) {
  const { currentColorPalette } = useWBTheme();
  const navigate = useNavigate();
  // Audit 2026-09-23 (4b): RegisterChild publishes the live outcome; the
  // props are only the seed a freshly created node starts from.
  const outcomeCtx = useContext(RegisterOutcomeContext);
  const isPendingApproval = outcomeCtx ? outcomeCtx.isPendingApproval : isPendingApprovalProp;
  const isWaitlisted = outcomeCtx ? outcomeCtx.isWaitlisted : isWaitlistedProp;
  const waitlistPosition = outcomeCtx ? outcomeCtx.waitlistPosition : waitlistPositionProp;

  const resolvedIconColor = isPendingApproval
    ? "#d97706"
    : (iconColor || "#16a34a");
  const resolvedIconBg = isPendingApproval
    ? "#fef3c7"
    : (iconBackgroundColor || "#dcfce7");

  const Icon = isPendingApproval ? IconClock : IconCheck;

  if (isWaitlisted) {
    return (
      <div
        className="flex flex-col items-center justify-center py-12 px-6 text-center"
        style={{ backgroundColor: backgroundColor || undefined }}
      >
        <div
          style={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            backgroundColor: "#fef3c7",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "1.5rem",
          }}
        >
          <IconClockHour4 size={44} color="#d97706" stroke={2.5} />
        </div>
        <h2
          style={{
            color: currentColorPalette.primary,
            fontSize: "1.5rem",
            fontWeight: 700,
            marginBottom: "0.5rem",
          }}
        >
          {t("waitlistTitle")}
        </h2>
        <p
          style={{
            color: currentColorPalette.secondary,
            fontSize: "1rem",
            lineHeight: "1.5",
            maxWidth: 360,
          }}
        >
          {t("waitlistSubtitle")}
        </p>
        {waitlistPosition != null && waitlistPosition > 0 ? (
          <p
            style={{
              color: currentColorPalette.primary,
              fontSize: "0.875rem",
              fontWeight: 600,
              marginTop: "0.75rem",
            }}
          >
            {t("waitlistPosition", String(waitlistPosition))}
          </p>
        ) : null}
        <button
          type="button"
          onClick={() => navigate("/login", { replace: true })}
          style={{
            marginTop: "1.5rem",
            padding: "0.625rem 2rem",
            backgroundColor: currentColorPalette.primary,
            color: "#ffffff",
            fontSize: "0.9375rem",
            fontWeight: 600,
            borderRadius: "0.375rem",
            border: "none",
            cursor: "pointer",
          }}
        >
          {t("close")}
        </button>
      </div>
    );
  }

  if (isPendingApproval) {
    return (
      <div
        className="flex flex-col items-center justify-center py-12 px-6 text-center"
        style={{ backgroundColor: backgroundColor || undefined }}
      >
        <div
          style={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            backgroundColor: resolvedIconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "1.5rem",
          }}
        >
          <Icon size={44} color={resolvedIconColor} stroke={2.5} />
        </div>
        <h2
          style={{
            color: currentColorPalette.primary,
            fontSize: "1.5rem",
            fontWeight: 700,
            marginBottom: "0.5rem",
          }}
        >
          {t("registrationSubmitted")}
        </h2>
        <p
          style={{
            color: currentColorPalette.secondary,
            fontSize: "1rem",
            lineHeight: "1.5",
            maxWidth: 360,
          }}
        >
          {t("pendingApprovalSubtitle")}
        </p>
        <button
          type="button"
          onClick={() => navigate("/login", { replace: true })}
          style={{
            marginTop: "1.5rem",
            padding: "0.625rem 2rem",
            backgroundColor: currentColorPalette.primary,
            color: "#ffffff",
            fontSize: "0.9375rem",
            fontWeight: 600,
            borderRadius: "0.375rem",
            border: "none",
            cursor: "pointer",
          }}
        >
          {t("close")}
        </button>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col items-center justify-center py-12 px-6 text-center"
      style={{ backgroundColor: backgroundColor || undefined }}
    >
      <div
        style={{
          width: 80,
          height: 80,
          borderRadius: "50%",
          backgroundColor: resolvedIconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "1.5rem",
        }}
      >
        <Icon size={44} color={resolvedIconColor} stroke={2.5} />
      </div>
      <div className="flex flex-col gap-2 w-full items-center">
        {children}
      </div>
    </div>
  );
}
